import React, {memo, useMemo} from "react"
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {format, parseISO} from "date-fns";
import {
    useReactTable,
    getCoreRowModel,
} from '@tanstack/react-table'

import DataTable from "../../../components/DataTable";
import {useModel1ValidationRegistryQuery} from "../query";

import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Icon from "@mui/material/Icon";

import Filters from "./Filters";
import {EditableHNAux, EditableParamCell, EditableStatusCell} from "./EditableCell";
import Tooltip from "@mui/material/Tooltip";
import StationCode from "../../../components/columns/StationCode";
import {WarningBox} from "./cell";
import {EditableParamHeaderCell, EditableStatusHeaderCell} from "./EditableHeader";


const cellWidth = "2rem";

export const statusIcons = {
    "OPEN": <Icon fontSize="small" color="warning">edit_document</Icon>,
    "CLOSED": <Icon fontSize="small" color="info">check</Icon>,
    "CONFIRMED": <Icon fontSize="small" color="success">lock</Icon>,
}

const validationStatusIcon = {
    "VALID": <Icon fontSize="small" color="success">check_circle</Icon>,
    "WARNING": <Icon fontSize="small" color="warning">warning</Icon>,
    "ERROR": <Icon fontSize="small" color="error">error</Icon>,
}


const getHNAux = (row) => {
    return [
        row?.hnR && "R",
        row?.hnT && "T",
        row?.hnW && "W",
        row?.hnMm && "MM",
        row?.hnMe && "ME",
        row?.hnG && "G"
    ].filter(x => x).join(",");
}

const Models1Table = (props) => {
    const {
        rows,
        page_count = -1,
        pagination,
        setPagination,
        onGlobalFilterChange,
        onFilterChange,
        sorting,
        onSortingChange,
        multipleEditing,
        model1Registry,
        updateData,
        updates,
        loading,
        headersState,
        handleHeaderChange,
        filterProps,
        visibleColumns,
        map
    } = props;
    const {t} = useTranslation(["models1", "enums"]);

    const model1ValidationRegistryQuery = useModel1ValidationRegistryQuery();
    const model1ValidationRegistry = model1ValidationRegistryQuery.data ?? {};

    const columns = useMemo(
        () => [
            {
                accessorKey: 'stationCode',  // this is the key used in filter and sorting
                cell: props => <StationCode station={props?.row?.original?.station} align="left"/>,
                header: () => <span>{t("table.station")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    align: "left",
                    width: "5rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'elevation',
                cell: props => `${props.getValue()} mt.`,
                header: () => <span>{t("table.elevation")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "4rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'validationStatus',
                cell: info => <ValidationStatusCol cell={info} status={info.getValue()}/>,
                header: () => <span>{t("table.validationStatus")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "3.5rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'status',
                cell: cell => <StatusCol key={`${cell.row?.original?.id}_${cell.cell.column.id}`}
                                         cell={cell}
                                         isEditing={cell.table.options?.meta.multipleEditing}
                                         updates={cell.table.options?.meta.updates}/>,
                header: header => <EditableStatusHeaderCell header={header}
                                                            label={t("table.status")}
                                                            onChange={handleHeaderChange}/>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "2.5rem",
                    px: "1px",
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'datetime',
                cell: props => <ParamCell cell={props}>{format(parseISO(props.row?.original?.datetime), "dd/MM/yyyy HH:mm")}</ParamCell>,
                header: () => <span>{t("table.datetime")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "8rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'period',
                cell: props => <PeriodCell cell={props}/>,
                header: () => <span>{t("table.period")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "3rem",
                    px: 0,
                    vAlign: "top",
                    verticalSeparator: true
                }
            },
            ...["ww", "n", "v", "vq1", "vq2", "ta", "tMin", "tMax", "hs", "hn", "hnAux", "hnCum", "hnStim", "ps", "th10", "th30", "pr", "pf", "sf", "sh", "l1", "l2", "l3", "l4", "l5", "l6", "l7", "l8", "l9"].map(param => {
                let headerLabel ;
                if (param === "hnCum") {
                    headerLabel = "hnc"
                } else if (param === "hnStim") {
                    headerLabel = "hns"
                } else {
                    headerLabel = param.replace("_", "");
                }
                if (param === "hnAux") {
                    return {
                        accessorKey: 'hnAux',
                        cell: cell => <HnAuxCell cell={cell}
                                                 key={`${cell.row?.original?.id}_${cell.cell.column.id}`}
                                                 isEditing={cell.table.options?.meta.multipleEditing}
                                                 updates={cell.table.options?.meta.updates}/>,
                        header: () => <span>HNAUX</span>,
                        enableSorting: false,
                        footer: props => props.column.id,
                        meta: {
                            align: "center",
                            width: "3rem",
                            px: "1px",
                            vAlign: "top"
                        }
                    }
                }
                else {
                    return {
                        accessorKey: param,
                        cell: cell => <ParamEditableCell key={`${cell.row?.original?.id}_${cell.cell.column.id}`}
                                                         cell={cell}
                                                         isEditing={cell.table.options?.meta.multipleEditing}
                                                         updates={cell.table.options?.meta.updates}/>,
                        header: (header) => <EditableParamHeaderCell header={header}
                                                                     label={headerLabel}
                                                                     onChange={handleHeaderChange}/>,
                        enableSorting: param === "hs" || param === "hn" || param === "l1",
                        footer: props => props.column.id,
                        meta: {
                            align: "center",
                            width: cellWidth,
                            px: "1px",
                            vAlign: "top",
                            headerTooltip: t(`table.${param}`),
                            verticalSeparator: param === "tMax" || param === "sh" || param === "l6" || param === "l9",
                        }
                    }
                }
            }),
            {
                accessorKey: 'notes',
                cell: props => <NoteCell cell={props}></NoteCell>,
                header: () => <span>{t("table.notes")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "left",
                    width: "6rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'user',
                cell: props => <UserCell cell={props}/>,
                header: () => <span>{t("table.user")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "4rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'Actions',
                cell: cell => <ActionCol cell={cell}
                                         multipleEditing={cell.table.options?.meta.multipleEditing}
                                         isDeleting={cell.table.options?.meta.deletes?.includes(cell.row.original.id)}/>,
                header: () => <span>{t("table.actions")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    px: 0,
                    vAlign: "top"
                }
            },
        ],
        []  // eslint-disable-line react-hooks/exhaustive-deps
    )

    // Re-render happen every time the data/rows change.
    // We re-create a new array because we want to control when the table is re-rendered,
    // and it should be re-rendered when either rows change or multipleEditing changes
    const data = useMemo(() => rows ? [...rows] : [], [rows, multipleEditing])

    const table = useReactTable({
        data: data,
        columns,
        pageCount: page_count ?? -1,
        state: {
            pagination,
            sorting,
            columnVisibility: visibleColumns,
        },
        onPaginationChange: setPagination,
        onGlobalFilterChange: onGlobalFilterChange,
        onSortingChange: onSortingChange,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,  // rows are expected to be paginated (controlled mode)
        manualSorting: true,  // rows are expected to be sorted (controlled mode)
        // getPaginationRowModel: getPaginationRowModel(), // If only doing manual pagination, you don't need this
        debugTable: true,
        meta: {
            multipleEditing: multipleEditing,
            model1Registry: model1Registry,
            model1ValidationRegistry: model1ValidationRegistry,
            updateData: updateData,
            deleteData: props.deleteData,
            updates: updates,
            deletes: props.deletes,
            headersState: headersState,
            map: map,
        }
    })

    const coloringOptions = useMemo(() => [
        {
            value: "none",
            getColor: (row) => {
                const isDeleting = table.options?.meta.deletes?.includes(row.original.id);
                return isDeleting ? "#ffebee" : "transparent";
            }
        },
        {
            value: "alternate",
            getColor: (row, i) => {
                const isDeleting = table.options?.meta.deletes?.includes(row.original.id);
                if (isDeleting) return "#ffebee";
                return i % 2 !== 0 ? "#f8f9fa" : "transparent";
            }
        },
        {
            value: "alternate_stations",
            label: t("table.coloring.alternateStations"),
            getColor: (row, i, allRows) => {
                const isDeleting = table.options?.meta.deletes?.includes(row.original.id);
                if (isDeleting) return "#ffebee"; // Light red

                if (i === 0) return "transparent";

                // We need to keep track of the color to alternate it
                let colorIndex = 0;
                for (let j = 1; j <= i; j++) {
                    if (allRows[j].original?.station?.id !== allRows[j - 1].original?.station?.id) {
                        colorIndex++;
                    }
                }
                return colorIndex % 2 !== 0 ? "#f8f9fa" : "transparent";
            }
        }
    ], [t, table.options?.meta.deletes]);

    return <DataTable dataTableId={props.dataTableId} table={table}
                      canSearch
                      searchPlaceholder={t("table.searchStation")}
                      showRowColoring
                      coloringOptions={coloringOptions}
                      rowColoring={props.rowColoring}
                      setRowColoring={props.setRowColoring}
                      stickyHeader={true}
                      filtersEl={<Filters onChange={onFilterChange}
                                          loading={loading}
                                          {...filterProps}/>}/>
}

const areEqualCellValues = (prevProps, nextProps) => {
    const prevValue = prevProps.cell.getValue();
    const nextValue = nextProps.cell.getValue();
    return prevValue === nextValue;
}


const areEqualEditableValues = (prevProps, nextProps) => {
    const prevRow = prevProps.cell.row.original.id;
    const nextRow = nextProps.cell.row.original.id;
    const prevCol = prevProps.cell.column.id;
    const nextCol = nextProps.cell.column.id;
    const prevValue = prevProps.cell.getValue();
    const nextValue = nextProps.cell.getValue();
    const prevUpdatedValue = prevProps.updates[prevRow]?.[prevCol];
    const nextUpdatedValue = nextProps.updates[nextRow]?.[nextCol];
    const prevValidationError = prevProps.cell.row.original.validationErrors;
    const nextValidationError = nextProps.cell.row.original.validationErrors;
    const prevValidationWarning = prevProps.cell.row.original.validationWarnings;
    const nextValidationWarning = nextProps.cell.row.original.validationWarnings;

    return (
        prevValue === nextValue
        && prevProps.isEditing === nextProps.isEditing
        && prevUpdatedValue === nextUpdatedValue
        && prevValidationError === nextValidationError
        && prevValidationWarning === nextValidationWarning
    );
}

const areEqualActionCol = (prevProps, nextProps) => {
    return prevProps.cell.row.original.id === nextProps.cell.row.original.id &&
        prevProps.multipleEditing === nextProps.multipleEditing &&
        prevProps.isDeleting === nextProps.isDeleting;
}

const ParamEditableCell = memo(({cell, isEditing, updates}) => {
    if (isEditing) {
        return <EditableParamCell cell={cell} />
    }
    return <ParamCell cell={cell}>{cell.getValue()}</ParamCell>
}, areEqualEditableValues)


const getHNAuxValues = (row) => {
    let values = []
    if (row.hnR) values.push("R");
    if (row.hnT) values.push("T");
    if (row.hnW) values.push("W");
    if (row.hnMm) values.push("Mm");
    if (row.hnMe) values.push("Me");
    if (row.hnG) values.push("G");
    return values;
}


const areEqualHNAux = (prevProps, nextProps) => {
    const prevRow = prevProps.cell.row.original;
    const nextRow = nextProps.cell.row.original;
    const prevValues = getHNAuxValues(prevRow).join(",");
    const nextValues = getHNAuxValues(nextRow).join(",");
    const prevUpdatedValues = prevProps.updates[prevRow.id] ? getHNAuxValues(prevProps.updates[prevRow.id]).join(",") : null;
    const nextUpdatedValues = nextProps.updates[nextRow.id] ? getHNAuxValues(nextProps.updates[nextRow.id]).join(",") : null;

    return prevValues === nextValues && prevProps.isEditing === nextProps.isEditing && prevUpdatedValues === nextUpdatedValues;
}

const HnAuxCell = memo(({cell, isEditing, updates}) => {
    const {t} = useTranslation("models1");

    const options = useMemo(() => [
        {value: "R", label: `R - ${t('table.hnR')}` },
        {value: "T", label: `T - ${t('table.hnT')}` },
        {value: "W", label: `W - ${t('table.hnW')}` },
        {value: "Mm", label: `Mm - ${t('table.hnMm')}` },
        {value: "Me", label: `Me - ${t('table.hnMe')}` },
        {value: "G", label: `G - ${t('table.hnG')}` },
    ], [t]);

    if (isEditing) {
        return <EditableHNAux cell={cell} options={options} />
    } else {
        const row = cell.row?.original ?? {};
        let values = []
        if (row.hnR) values.push("R");
        if (row.hnT) values.push("T");
        if (row.hnW) values.push("W");
        if (row.hnMm) values.push("Mm");
        if (row.hnMe) values.push("Me");
        if (row.hnG) values.push("G");

        const tooltip = values.map(v => {
            const option = options.find(o => o.value === v);
            return option ? option.label : v;
        }).join(",\n ");

        return <Tooltip title={<div style={{whiteSpace: "pre"}}>{tooltip}</div>} placement="top" disableInteractive>
            {getHNAux(cell.row.original)}
        </Tooltip>
    }
}, areEqualHNAux);

const ParamCell = ({cell, children}) => {
    const validationErrors = cell.row?.original?.validationErrors[`${cell.column.id}`] ?? [];
    const validationWarnings = cell.row?.original?.validationWarnings[`${cell.column.id}`] ?? [];

    if (validationErrors.length > 0 || validationWarnings.length > 0) {
        const model1ValidationRegistry = cell.table.options?.meta.model1ValidationRegistry ?? {};
        const message = [...validationErrors, ...validationWarnings].map(e => model1ValidationRegistry[e] ?? e).join(", ");
        const severity = validationErrors.length > 0 ? "error" : "warning";
        return <Tooltip title={message} placement="bottom" disableInteractive>
            <WarningBox severity={severity}>
                {children}
            </WarningBox>
        </Tooltip>
    }
    return children;
}

const NoteCell = memo(({cell}) => {
    const value = cell.getValue();
    if (value !== "" && value != null) {
        return <Tooltip title={value} placement="bottom" disableInteractive>
            <span>{value.length > 16 ? value.substring(0, 15) + "..." : value}</span>
        </Tooltip>
    } else {
        return null;
    }
}, areEqualCellValues)


const PeriodCell = memo(({cell}) => {
    const {t} = useTranslation(['enums']);

    const value = cell.getValue();
    if (value === "H24") {
        return <Tooltip title={t('model1Period.H24')} placement="bottom" disableInteractive>
            <span>24h</span>
        </Tooltip>;
    } else if (value === "LT24H") {
        return <Tooltip title={t('model1Period.LT24H')} placement="bottom" disableInteractive>
            <span>{"<24h"}</span>
        </Tooltip>;
    } else if (value === "GT24H") {
        return <Tooltip title={t('model1Period.GT24H')} placement="bottom" disableInteractive>
            <span>{">24h"}</span>
        </Tooltip>;
    } else {
        return "";
    }
}, areEqualCellValues);

const UserCell = memo(({cell}) => {
    const user = cell.row.original.user ?? {};
    const fullName = user ? `${user?.surname} ${user.name}` : "";

    if (fullName !== "" && fullName != null) {
        return <Tooltip title={fullName} placement="bottom" disableInteractive>
            <span>{fullName.length > 8 ? fullName.substring(0, 7) + "..." : fullName}</span>
        </Tooltip>
    } else {
        return null;
    }
}, (prevProps, nextProps) => {
    return prevProps.cell.row.original.user?.id === nextProps.cell.row.original.user?.id;
});

const ActionCol = memo(({cell, multipleEditing, isDeleting}) => {
    const navigate = useNavigate();
    const model1 = cell.row.original;
    const {id} = model1;
    const map = cell.table.options?.meta.map;
    const pos = {lat: model1.geom.coordinates[1], lng: model1.geom.coordinates[0]}
    const deleteData = cell.table.options?.meta.deleteData;

    return <Stack direction="row" spacing={0.5} justifyContent="center">
        <IconButton onClick={() => map.flyTo(pos)} sx={{p: 0}}>
            <Icon fontSize="small">location_searching</Icon>
        </IconButton>
        {multipleEditing ? (
            <IconButton onClick={() => deleteData(id)} sx={{p: 0}}>
                <Icon fontSize="small" color={isDeleting ? "info" : "error"}>
                    {isDeleting ? "undo" : "delete"}
                </Icon>
            </IconButton>
        ) : (
            <IconButton onClick={() => navigate(`/models1/${id}/edit`)} sx={{p: 0}}>
                <Icon fontSize="small">edit</Icon>
            </IconButton>
        )}
    </Stack>
}, areEqualActionCol)

const StatusCol = memo(({cell, isEditing, updates}) => {
    const status = cell.getValue();
    const {t} = useTranslation(['enums']);

    if (cell.table.options?.meta.multipleEditing) {
        return <EditableStatusCell cell={cell} />
    } else if (statusIcons[status]) {
        return <Tooltip title={t(`model1Status.${status}`)} placement="bottom" disableInteractive>
            {statusIcons[status]}
        </Tooltip>;
    }
    return null
}, areEqualEditableValues)

const ValidationStatusCol = memo(({cell}) => {
    const status = cell.getValue();
    if (validationStatusIcon[status]) {
        if ((status === "WARNING" || status === "ERROR")) {
            const validationErrors = Object.values(cell.row?.original?.validationErrors).flatMap((codes) => codes);
            const validationWarnings = Object.values(cell.row?.original?.validationWarnings).flatMap((codes) => codes);
            const model1ValidationRegistry = cell.table.options?.meta.model1ValidationRegistry ?? {};
            const message = [...validationErrors, ...validationWarnings].map(e => model1ValidationRegistry[e] ?? e).join(", ");
            return <Tooltip title={message} placement="bottom" disableInteractive>
                {validationStatusIcon[status]}
            </Tooltip>
        } else {
            return validationStatusIcon[status];
        }
    }
    return null
}, areEqualCellValues)

export default Models1Table;